import {
  Button,
  FileUpload,
  Input,
  Progress,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import "@uploadcare/react-uploader/core.css";
import { FileUploaderMinimal } from "@uploadcare/react-uploader/next";
import * as React from "react";
import { useForm } from "react-hook-form";
import { LuPlus, LuUpload } from "react-icons/lu";
import { z } from "zod";

import { Upload } from "../api/uploads";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "./chakra/dialog";
import { Field } from "./chakra/field";
import "./file-dialog.css";

const fileFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  file: z.instanceof(File).optional(),
  thumbnails: z.array(z.string()).optional(),
});

export type FileFormData = z.infer<typeof fileFormSchema>;

export enum FileDialogMode {
  Upload = "upload",
  Edit = "edit",
}

interface FileDialogProps {
  buttonSize?: "xs" | "sm" | "md" | "lg";
  file?: Upload;
  isLoading: boolean;
  mode: FileDialogMode;
  onOpenChange: (open: boolean) => void;
  onSave: (
    data: FileFormData,
    onProgress?: (progress: number) => void,
  ) => Promise<void>;
  open: boolean;
}

export const FileDialog = ({
  buttonSize = "sm",
  file,
  isLoading,
  open,
  mode,
  onOpenChange,
  onSave,
}: FileDialogProps) => {
  return (
    <DialogRoot
      size="sm"
      open={open}
      onOpenChange={({ open }) => onOpenChange(open)}
    >
      <DialogBackdrop />
      {mode === FileDialogMode.Upload && (
        <DialogTrigger asChild>
          <Button colorPalette="primary" size={buttonSize}>
            <LuPlus />
            Upload File
          </Button>
        </DialogTrigger>
      )}
      <FileDialogContent
        mode={mode}
        loading={isLoading}
        onSave={onSave}
        file={file}
      />
    </DialogRoot>
  );
};

interface FileDialogContentProps {
  file?: Upload;
  loading: boolean;
  mode: FileDialogMode;
  onSave: (
    data: FileFormData,
    onProgress?: (progress: number) => void,
  ) => Promise<void>;
}

const FileDialogContent = ({
  mode,
  loading,
  onSave,
  file,
}: FileDialogContentProps) => {
  const [progress, setProgress] = React.useState<number | null>(null);
  const [thumbnailsUploading, setThumbnailsUploading] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FileFormData>({
    resolver: zodResolver(fileFormSchema),
    defaultValues: file
      ? {
          title: file.title,
          description: file.description,
          thumbnails: file.thumbnails,
        }
      : undefined,
  });

  const onSubmit = handleSubmit((data) => {
    setProgress(0);
    return onSave(data, setProgress);
  });

  return (
    <DialogContent>
      <form onSubmit={onSubmit}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {mode === FileDialogMode.Upload ? "Upload File" : "Edit File"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4">
            <Field
              label="Title"
              invalid={!!errors.title}
              errorText={errors.title?.message}
            >
              <Input {...register("title")} />
            </Field>
            <Field
              label="Description"
              invalid={!!errors.description}
              errorText={errors.description?.message}
            >
              <Textarea {...register("description")} />
            </Field>
            <Field
              label="File"
              invalid={!!errors.file}
              errorText={errors.file?.message}
            >
              <FileUpload.Root
                alignItems="stretch"
                maxFiles={1}
                onFileChange={(details) => {
                  if (details.acceptedFiles.length > 0) {
                    setValue("file", details.acceptedFiles[0]);
                  }
                }}
              >
                <FileUpload.HiddenInput />
                <FileUpload.Dropzone>
                  <FileUpload.DropzoneContent>
                    <Stack alignItems="center" flex="1">
                      <LuUpload />
                      Drag and drop your file here or click to browse.
                      <Text fontSize="sm" color="gray.500">
                        Files can be up to 1GB large.
                      </Text>
                    </Stack>
                  </FileUpload.DropzoneContent>
                </FileUpload.Dropzone>
                <FileUpload.List />
                {loading && progress !== null && (
                  <Stack gap="2" mt="2">
                    <Text fontSize="sm" color="gray.600">
                      Upload Progress: {Math.round(progress * 100)}%
                    </Text>
                    <Progress.Root value={progress * 100}>
                      <Progress.Track>
                        <Progress.Range />
                      </Progress.Track>
                    </Progress.Root>
                  </Stack>
                )}
              </FileUpload.Root>
            </Field>
            <Field label="Thumbnails (optional)" w="full">
              <FileUploaderMinimal
                className="upload-dialog__thumbnails"
                classNameUploader="uc-light"
                filesViewMode="grid"
                onChange={(state) => {
                  setThumbnailsUploading(state.uploadingEntries.length > 0);
                  setValue(
                    "thumbnails",
                    state.successEntries.map((entry) => entry.cdnUrl),
                  );
                }}
                removeCopyright
                pubkey="94b3a8886cbe6c8c17d4"
                sourceList="local, camera, facebook, gdrive"
              />
            </Field>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button colorPalette="gray" variant="outline">
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            type="submit"
            loading={loading}
            disabled={thumbnailsUploading}
            colorPalette="primary"
          >
            {mode === FileDialogMode.Upload ? "Upload" : "Save Changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
